import { computed } from 'vue'
import type { ComputedRef, Ref } from 'vue'
import { evaluateFocusedEntryPointRules } from '@shared/assessment-definition/dist/src/focused-plan'
import type {
  FocusedPlan,
  PlanEntryPointRule,
  PlanLeaf,
  PlanTriggerExclusion,
} from '@shared/assessment-definition/dist/src/focused-plan'
import type { useGuidedModeStore } from '../stores/guidedMode'
import type { GuidedModeAssumption } from '../stores/guidedMode'
import type {
  GuidedAnswer,
  GuidedAssessmentPath,
  GuidedModeDefinition,
  GuidedModeStep,
  GuidedModeSymptom,
} from '../types/guided-mode.types'

interface UseGuidedEntryPointRulesOptions {
  store: ReturnType<typeof useGuidedModeStore>
  answers: Ref<Record<string, GuidedAnswer>>
  activeAssessmentPath: Ref<GuidedAssessmentPath | null>
  definition: ComputedRef<GuidedModeDefinition | null>
  excludedCodes: ComputedRef<string[]>
  symptomById: ComputedRef<Map<string, GuidedModeSymptom>>
  stepById: ComputedRef<Map<string, GuidedModeStep>>
  planLeafEntries: ComputedRef<PlanLeaf[]>
  leafIdByConceptId: ComputedRef<Map<string, string>>
}

function hasAnswer(answer: GuidedAnswer | undefined): boolean {
  return Array.isArray(answer) ? answer.length > 0 : answer !== undefined && answer !== null && answer !== ''
}

function assumptionTarget(assumption: GuidedModeAssumption, symptom: GuidedModeSymptom) {
  return {
    conceptId: assumption.conceptId || symptom.conceptId,
    code: assumption.code || symptom.code,
  }
}

function restoreAssumption(
  nextAnswers: Record<string, GuidedAnswer>,
  conceptId: string,
  assumedValue: GuidedAnswer,
  assumption: GuidedModeAssumption,
): void {
  if (assumption.overridden || nextAnswers[conceptId] !== assumedValue) return
  if (assumption.hadPrevious) nextAnswers[conceptId] = assumption.previous!
  else delete nextAnswers[conceptId]
}

export function useGuidedEntryPointRules({
  store,
  answers,
  activeAssessmentPath,
  definition,
  excludedCodes,
  symptomById,
  stepById,
  planLeafEntries,
  leafIdByConceptId,
}: UseGuidedEntryPointRulesOptions) {
  const values = computed(() => {
    const concepts = definition.value?.concepts || {}
    return Object.fromEntries(Object.entries(answers.value).map(([conceptId, answer]) => [
      concepts[conceptId]?.code?.code || conceptId.replace(/^concept\./, ''),
      answer,
    ]))
  })
  const recordedEntryPointIds = computed(() => store.recordedEntryPointSymptomIds.filter(id => {
    const symptom = symptomById.value.get(id)
    return Boolean(symptom) && answers.value[symptom!.conceptId] === symptom!.value
  }))
  const explicitSymptomIds = computed(() => new Set([
    ...store.startedFocusedAssessments.map(symptom => symptom.id),
    ...recordedEntryPointIds.value,
  ]))
  const activeAssumeIds = computed(() => store.committedAssumeSymptomIds.filter(id => (
    !store.assumedSymptomAnswers[id]?.overridden
  )))
  const activeAskIds = computed(() => store.committedAskSymptomIds.filter(id => {
    const symptom = symptomById.value.get(id)
    return Boolean(symptom) && store.confirmedAskSymptomIds.includes(id)
      && answers.value[symptom!.conceptId] === symptom!.value
  }))
  const askStepIds = computed(() => store.committedAskSymptomIds.flatMap(id => {
    if (store.confirmedAskSymptomIds.includes(id)) return []
    const symptom = symptomById.value.get(id)
    const stepId = symptom ? leafIdByConceptId.value.get(symptom.conceptId) : undefined
    return stepId ? [stepId] : []
  }))
  const selectedSymptomIds = computed(() => Array.from(new Set([
    ...store.startedFocusedAssessments.map(symptom => symptom.id),
    ...recordedEntryPointIds.value,
    ...activeAssumeIds.value,
    ...activeAskIds.value,
  ])))
  const triggerExclusions = computed<PlanTriggerExclusion[]>(() => {
    const exclusions = Object.entries(store.introducedFocusedStepOrigins).flatMap(([stepId, symptomIds]) => {
      const code = planLeafEntries.value.find(leaf => leaf.id === stepId)?.code
      return code ? symptomIds.map(symptomId => ({ symptomId, questionCode: code })) : []
    })
    for (const [symptomId, assumption] of Object.entries(store.assumedSymptomAnswers)) {
      const symptom = symptomById.value.get(symptomId)
      if (symptom) exclusions.push({
        symptomId,
        questionCode: assumptionTarget(assumption, symptom).code,
      })
    }
    return exclusions
  })

  function commit(): void {
    const path = activeAssessmentPath.value
    if (!path) return
    const rules = store.clinicalContent.entryPointRules?.[path] || []
    const matches = evaluateFocusedEntryPointRules(
      rules as PlanEntryPointRule[],
      values.value,
      triggerExclusions.value,
    )
    const explicit = explicitSymptomIds.value
    const matchedAssume = new Set(matches.assume.filter(id => !explicit.has(id)))
    const matchedAsk = new Set(matches.ask.filter(id => !explicit.has(id)))
    const previousAsk = new Set(store.committedAskSymptomIds)
    const assumptions = { ...store.assumedSymptomAnswers }
    const nextAnswers = { ...answers.value }
    const transitionedToAsk = new Set<string>()
    const recordedEntryPoints = new Set(recordedEntryPointIds.value)

    for (const [symptomId, assumption] of Object.entries(assumptions)) {
      const symptom = symptomById.value.get(symptomId)
      if (!symptom) {
        delete assumptions[symptomId]
        continue
      }
      const target = assumptionTarget(assumption, symptom)
      const storedAssumption: GuidedModeAssumption = {
        ...assumption,
        conceptId: target.conceptId,
        code: target.code,
      }
      const entryChanged = target.conceptId !== symptom.conceptId || target.code !== symptom.code
      if (entryChanged) {
        restoreAssumption(nextAnswers, target.conceptId, symptom.value, storedAssumption)
        delete assumptions[symptomId]
        if (matchedAsk.has(symptomId)) transitionedToAsk.add(symptomId)
        continue
      }
      if (matchedAssume.has(symptomId)) {
        assumptions[symptomId] = !storedAssumption.overridden
          && nextAnswers[target.conceptId] !== symptom.value
          ? { ...storedAssumption, overridden: true }
          : storedAssumption
        continue
      }
      restoreAssumption(nextAnswers, target.conceptId, symptom.value, storedAssumption)
      delete assumptions[symptomId]
      if (matchedAsk.has(symptomId)) transitionedToAsk.add(symptomId)
    }

    for (const symptomId of matchedAssume) {
      if (assumptions[symptomId]) continue
      const symptom = symptomById.value.get(symptomId)
      if (!symptom) continue
      const previous = nextAnswers[symptom.conceptId]
      // A rule can fill an unanswered finding, but cannot replace a collected answer.
      if (hasAnswer(previous) && previous !== symptom.value) continue
      const targetStepId = leafIdByConceptId.value.get(symptom.conceptId)
      const introducedBySymptom = Boolean(targetStepId
        && store.introducedFocusedStepOrigins[targetStepId]?.includes(symptomId))
      if (previous === symptom.value && !introducedBySymptom) {
        recordedEntryPoints.add(symptomId)
        continue
      }
      const assumption: GuidedModeAssumption = {
        conceptId: symptom.conceptId,
        code: symptom.code,
        hadPrevious: hasAnswer(previous),
      }
      if (assumption.hadPrevious) assumption.previous = previous
      assumptions[symptomId] = assumption
      nextAnswers[symptom.conceptId] = symptom.value
    }

    const confirmedAsk = new Set(store.confirmedAskSymptomIds.filter(id => {
      const symptom = symptomById.value.get(id)
      return Boolean(symptom) && hasAnswer(nextAnswers[symptom!.conceptId])
    }))
    for (const symptomId of matchedAsk) {
      const symptom = symptomById.value.get(symptomId)
      if (!symptom) continue
      if (transitionedToAsk.has(symptomId)) {
        confirmedAsk.delete(symptomId)
        continue
      }
      if (previousAsk.has(symptomId) && hasAnswer(nextAnswers[symptom.conceptId])) {
        confirmedAsk.add(symptomId)
        continue
      }
      const targetStepId = leafIdByConceptId.value.get(symptom.conceptId)
      const introducedBySymptom = Boolean(targetStepId
        && store.introducedFocusedStepOrigins[targetStepId]?.includes(symptomId))
      if (!introducedBySymptom && hasAnswer(nextAnswers[symptom.conceptId])) confirmedAsk.add(symptomId)
    }

    store.assumedSymptomAnswers = assumptions
    store.committedAssumeSymptomIds = Array.from(matchedAssume).filter(id => (
      !recordedEntryPoints.has(id) && Boolean(assumptions[id]) && !assumptions[id]?.overridden
    ))
    store.committedAskSymptomIds = Array.from(matchedAsk)
    store.confirmedAskSymptomIds = Array.from(confirmedAsk)
    store.recordedEntryPointSymptomIds = Array.from(recordedEntryPoints)
    store.triggeredSymptomIds = Array.from(new Set([
      ...store.committedAssumeSymptomIds,
      ...store.committedAskSymptomIds.filter(id => confirmedAsk.has(id)
        && nextAnswers[symptomById.value.get(id)?.conceptId || ''] === symptomById.value.get(id)?.value),
    ]))
    if (JSON.stringify(nextAnswers) !== JSON.stringify(answers.value)) answers.value = nextAnswers
  }

  function recordIntroducedSteps(
    plan: FocusedPlan | null,
    previousFocusedStepIds: string[] | null,
    nextFocusedStepIds: string[],
  ): void {
    const currentDefinition = definition.value
    if (!currentDefinition) return
    const dynamicSymptomIds = new Set([...activeAssumeIds.value, ...activeAskIds.value])
    const introduced = new Map<string, Set<string>>()
    const origins = Object.fromEntries(Object.entries(store.introducedFocusedStepOrigins)
      .map(([id, symptomIds]) => [id, [...symptomIds]]))
    const stepHasAnswer = (stepId: string) => {
      const step = stepById.value.get(stepId)
      const conceptId = step?.questionId ? currentDefinition.questions[step.questionId]?.conceptId : undefined
      return Boolean(conceptId) && hasAnswer(answers.value[conceptId!])
    }
    const addOrigins = (stepId: string, symptomIds: Iterable<string>, force = false) => {
      if (!force && !origins[stepId] && stepHasAnswer(stepId)) return
      const introducedOrigins = introduced.get(stepId) ?? new Set<string>()
      for (const symptomId of symptomIds) introducedOrigins.add(symptomId)
      introduced.set(stepId, introducedOrigins)
    }
    for (const symptomId of store.committedAskSymptomIds) {
      if (store.confirmedAskSymptomIds.includes(symptomId)) continue
      const symptom = symptomById.value.get(symptomId)
      const stepId = symptom ? leafIdByConceptId.value.get(symptom.conceptId) : undefined
      if (stepId) addOrigins(stepId, [symptomId], true)
    }
    for (const wave of plan?.waves || []) {
      for (const page of wave.pages) {
        if (dynamicSymptomIds.has(page.symptomId)) {
          page.stepIds.forEach(id => addOrigins(id, [page.symptomId]))
        }
      }
      const dynamicWaveIds = wave.symptomIds.filter(id => dynamicSymptomIds.has(id))
      if (dynamicWaveIds.length) {
        wave.vitalSignStepIds.forEach(id => addOrigins(id, dynamicWaveIds))
      }
    }
    if (previousFocusedStepIds !== null && dynamicSymptomIds.size > 0) {
      const previous = new Set(previousFocusedStepIds)
      nextFocusedStepIds.filter(id => !previous.has(id)).forEach(id => addOrigins(id, dynamicSymptomIds))
    }
    const excluded = new Set(excludedCodes.value)
    const leafById = new Map(planLeafEntries.value.map(leaf => [leaf.id, leaf]))
    const ordered = Array.from(introduced.keys()).filter(id => {
      const leaf = leafById.get(id)
      return leaf && !(leaf.code && excluded.has(leaf.code))
    })
    for (const id of ordered) {
      origins[id] = Array.from(new Set([...(origins[id] || []), ...introduced.get(id)!]))
    }
    const mustConfirm = new Set(askStepIds.value)
    const pending = ordered.filter(id => {
      const step = stepById.value.get(id)
      const conceptId = step?.questionId ? currentDefinition.questions[step.questionId]?.conceptId : undefined
      return Boolean(conceptId) && (mustConfirm.has(id) || !hasAnswer(answers.value[conceptId!]))
    })
    const retained = new Set([...ordered, ...Object.keys(origins).filter(id => {
      const step = stepById.value.get(id)
      const conceptId = step?.questionId ? currentDefinition.questions[step.questionId]?.conceptId : undefined
      return Boolean(conceptId) && hasAnswer(answers.value[conceptId!])
    })])
    for (const id of Object.keys(origins)) {
      if (!retained.has(id)) delete origins[id]
    }
    store.introducedFocusedStepOrigins = origins
    store.introducedFocusedStepIds = planLeafEntries.value
      .filter(leaf => retained.has(leaf.id))
      .map(leaf => leaf.id)
    store.pendingFocusedStepIds = pending
  }

  function reset(): void {
    store.triggeredSymptomIds = []
    store.pendingFocusedStepIds = []
    store.focusedResumeStepKey = null
    store.introducedFocusedStepIds = []
    store.introducedFocusedStepOrigins = {}
    store.committedAssumeSymptomIds = []
    store.committedAskSymptomIds = []
    store.confirmedAskSymptomIds = []
    store.recordedEntryPointSymptomIds = []
    store.assumedSymptomAnswers = {}
  }

  return {
    values,
    recordedEntryPointIds,
    activeAssumeIds,
    activeAskIds,
    askStepIds,
    selectedSymptomIds,
    triggerExclusions,
    commit,
    recordIntroducedSteps,
    reset,
  }
}
